import { useConsentStore } from "@/components/analytics/consent";
import {
  trackContentView,
  trackPendingAccountRegistration,
} from "@/components/analytics/conversions";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

const configured = (value: string | undefined) =>
  value && value !== "not-set" ? value : "";
const GA_ID =
  configured(import.meta.env.VITE_GA_MEASUREMENT_ID) ||
  (import.meta.env.VITE_APP_STAGE === "production" ? "G-Q2K575TNPJ" : "");
const GOOGLE_ADS_ID =
  configured(import.meta.env.VITE_GOOGLE_ADS_ID) ||
  (import.meta.env.VITE_APP_STAGE === "production" ? "AW-16798392352" : "");

export function initializeGoogle() {
  if (window.gtag || (!GA_ID && !GOOGLE_ADS_ID)) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => window.dataLayer?.push(args);
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  window.gtag("js", new Date());
  if (GA_ID) window.gtag("config", GA_ID, { send_page_view: false });
  if (GOOGLE_ADS_ID) window.gtag("config", GOOGLE_ADS_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID || GOOGLE_ADS_ID}`;
  document.head.appendChild(script);
}

export function GoogleAnalytics() {
  const location = useLocation();
  const enabled = useConsentStore((s) => s.consent === "granted");
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (window.gtag)
      window.gtag("consent", "update", {
        ad_storage: enabled ? "granted" : "denied",
        ad_user_data: enabled ? "granted" : "denied",
        ad_personalization: enabled ? "granted" : "denied",
        analytics_storage: enabled ? "granted" : "denied",
      });
    if (!enabled) return;
    initializeGoogle();
    trackPendingAccountRegistration();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) previousPath.current = null;
    if (!enabled || import.meta.env.DEV || !window.gtag) return;
    const path = `${location.pathname}${location.searchStr}${location.hash}`;
    if (previousPath.current === path) return;
    previousPath.current = path;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });

    if (location.pathname === "/projects") {
      trackContentView("projects", "projects_index");
    } else if (
      location.pathname.startsWith("/essay/") &&
      !location.pathname.endsWith("-calculator")
    ) {
      trackContentView("essay", location.pathname.slice("/essay/".length));
    }
  }, [enabled, location.hash, location.pathname, location.searchStr]);

  return null;
}

declare global {
  interface Window {
    dataLayer?: unknown[][];
  }
}
